import React, { useState, useEffect, useContext, useCallback } from "react";
import {
  Paper, Typography, Button, TextField, MenuItem, IconButton, Box,
  Table, TableHead, TableBody, TableRow, TableCell, Tooltip, Divider
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import AddIcon from "@material-ui/icons/Add";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import { toast } from "react-toastify";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles((theme) => ({
  painel: { padding: theme.spacing(3), marginBottom: theme.spacing(2) },
  passo: { fontWeight: 600, marginBottom: theme.spacing(1) },
  campoUrl: { fontFamily: "monospace", fontSize: 13 },
  ajuda: { color: "#667781", fontSize: 13, marginBottom: theme.spacing(2) },
}));

const TIPOS = [
  { v: "plan", t: "Oferta de assinatura (plano)" },
  { v: "offer", t: "Oferta do produto" },
  { v: "product", t: "Produto inteiro" },
];

const EiPaguei = () => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);

  const [config, setConfig] = useState(null);
  const [planos, setPlanos] = useState([]);
  const [vinculos, setVinculos] = useState([]);
  const [tokenNovo, setTokenNovo] = useState("");
  const [urlCheckout, setUrlCheckout] = useState("");
  const [salvando, setSalvando] = useState(false);

  const carregar = useCallback(async () => {
    try {
      const [{ data: cfg }, { data: pls }] = await Promise.all([
        api.get("/eipaguei/config"),
        api.get("/plans/list"),
      ]);
      setConfig(cfg);
      setVinculos(cfg.vinculos || []);
      setUrlCheckout(cfg.urlCheckout || "");
      setPlanos(Array.isArray(pls) ? pls : pls?.plans || []);
    } catch (e) {
      toastError(e);
    }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  const gerarToken = async () => {
    if (config?.temToken &&
      !window.confirm("Já existe um token. Gerar outro faz o Ei Paguei parar de ser aceito até você colar o novo lá. Continuar?")) return;
    try {
      const { data } = await api.post("/eipaguei/token");
      setTokenNovo(data.token);
      toast.success("Token gerado. Copie agora — ele não aparece de novo.");
      carregar();
    } catch (e) { toastError(e); }
  };

  const copiar = (texto) => {
    if (!texto) return;
    if (navigator.clipboard) {
      navigator.clipboard.writeText(texto);
      toast.success("Copiado.");
    }
  };

  const salvar = async () => {
    setSalvando(true);
    try {
      await api.put("/eipaguei/config", { vinculos, urlCheckout });
      toast.success("Vínculos salvos.");
      carregar();
    } catch (e) { toastError(e); }
    setSalvando(false);
  };

  const alterar = (i, campo, valor) => {
    setVinculos((atual) => atual.map((v, idx) => idx === i ? { ...v, [campo]: valor } : v));
  };

  if (user?.super !== true) {
    return (
      <MainContainer>
        <Typography style={{ padding: 24 }}>
          Esta tela é do dono da plataforma.
        </Typography>
      </MainContainer>
    );
  }

  return (
    <MainContainer>
      <MainHeader>
        <Title>Cobrança pelo Ei Paguei</Title>
      </MainHeader>

      <Paper className={classes.painel} variant="outlined">
        <Typography className={classes.passo}>1. Endereço do aviso</Typography>
        <Typography className={classes.ajuda}>
          Cole este endereço no Ei Paguei, em Webhooks.
        </Typography>
        <Box display="flex" alignItems="center" style={{ gap: 8 }}>
          <TextField
            fullWidth variant="outlined" size="small"
            value={config?.urlWebhook || ""}
            InputProps={{ readOnly: true, className: classes.campoUrl }}
          />
          <Tooltip title="Copiar">
            <IconButton onClick={() => copiar(config?.urlWebhook)}>
              <FileCopyIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Paper>

      <Paper className={classes.painel} variant="outlined">
        <Typography className={classes.passo}>2. Token de segurança</Typography>
        <Typography className={classes.ajuda}>
          Sem ele qualquer um poderia liberar assinaturas de graça. O Ei Paguei
          manda esse token em toda chamada e o Floow Chat recusa quem não tiver.
        </Typography>

        {tokenNovo ? (
          <Box display="flex" alignItems="center" style={{ gap: 8 }}>
            <TextField
              fullWidth variant="outlined" size="small" value={tokenNovo}
              InputProps={{ readOnly: true, className: classes.campoUrl }}
            />
            <Tooltip title="Copiar">
              <IconButton onClick={() => copiar(tokenNovo)}><FileCopyIcon /></IconButton>
            </Tooltip>
          </Box>
        ) : (
          <Typography style={{ marginBottom: 12 }}>
            {config?.temToken
              ? `Token configurado, terminado em ${config.finalDoToken}`
              : "Nenhum token configurado ainda."}
          </Typography>
        )}

        <Button variant="outlined" color="primary" onClick={gerarToken} style={{ marginTop: 12 }}>
          {config?.temToken ? "Gerar um novo token" : "Gerar token"}
        </Button>
      </Paper>

      <Paper className={classes.painel} variant="outlined">
        <Typography className={classes.passo}>3. De qual produto vem cada plano</Typography>
        <Typography className={classes.ajuda}>
          O Ei Paguei vende vários produtos. Aqui você diz quais deles são
          assinatura do Floow Chat e qual plano cada um libera. Compra de
          produto que não estiver nesta lista é ignorada.
        </Typography>

        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>O que casar</TableCell>
              <TableCell>ID no Ei Paguei</TableCell>
              <TableCell>Libera o plano</TableCell>
              <TableCell>Meses</TableCell>
              <TableCell align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            {vinculos.map((v, i) => (
              <TableRow key={i}>
                <TableCell>
                  <TextField
                    select fullWidth variant="outlined" size="small"
                    value={v.tipo || "plan"}
                    onChange={(e) => alterar(i, "tipo", e.target.value)}
                  >
                    {TIPOS.map((t) => (
                      <MenuItem key={t.v} value={t.v}>{t.t}</MenuItem>
                    ))}
                  </TextField>
                </TableCell>
                <TableCell>
                  <TextField
                    fullWidth variant="outlined" size="small"
                    value={v.idExterno || ""}
                    onChange={(e) => alterar(i, "idExterno", e.target.value)}
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    select fullWidth variant="outlined" size="small"
                    value={v.planId || ""}
                    onChange={(e) => alterar(i, "planId", e.target.value)}
                  >
                    {planos.map((p) => (
                      <MenuItem key={p.id} value={p.id}>
                        {p.name} — R$ {p.amount}
                      </MenuItem>
                    ))}
                  </TextField>
                </TableCell>
                <TableCell style={{ width: 90 }}>
                  <TextField
                    type="number" fullWidth variant="outlined" size="small"
                    value={v.meses || 1}
                    onChange={(e) => alterar(i, "meses", e.target.value)}
                  />
                </TableCell>
                <TableCell align="right">
                  <IconButton size="small"
                    onClick={() => setVinculos(vinculos.filter((_, idx) => idx !== i))}>
                    <DeleteOutlineIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        <Box mt={2} display="flex" style={{ gap: 8 }}>
          <Button
            startIcon={<AddIcon />}
            onClick={() => setVinculos([...vinculos, { tipo: "plan", idExterno: "", planId: "", meses: 1 }])}
          >
            Adicionar
          </Button>
          <Button variant="contained" color="primary" onClick={salvar} disabled={salvando}>
            {salvando ? "Salvando..." : "Salvar"}
          </Button>
        </Box>
      </Paper>

      <Paper className={classes.painel} variant="outlined">
        <Typography className={classes.passo}>4. Endereço do checkout</Typography>
        <Typography className={classes.ajuda}>
          É para onde o cliente vai quando clica em "Assinar agora" no painel
          dele. Cole aqui o link do produto no Ei Paguei.
        </Typography>
        <TextField
          fullWidth variant="outlined" size="small"
          placeholder="https://app.eipaguei.com.br/seu-produto"
          value={urlCheckout}
          onChange={(e) => setUrlCheckout(e.target.value)}
          InputProps={{ className: classes.campoUrl }}
        />
        <Button variant="contained" color="primary" onClick={salvar}
          disabled={salvando} style={{ marginTop: 12 }}>
          {salvando ? "Salvando..." : "Salvar"}
        </Button>
      </Paper>

      <Paper className={classes.painel} variant="outlined">
        <Typography className={classes.passo}>5. Quais avisos marcar no Ei Paguei</Typography>
        <Divider style={{ marginBottom: 12 }} />
        <Typography component="div" style={{ fontSize: 14, lineHeight: 2 }}>
          <b>Libera ou renova:</b> Pedido pago, Assinatura criada, Assinatura renovada<br />
          <b>Bloqueia:</b> Reembolso, Pedido cancelado, Assinatura cancelada
        </Typography>
        <Typography className={classes.ajuda} style={{ marginTop: 12 }}>
          Cliente que ainda não tem conta recebe empresa, usuário e senha por
          e-mail automaticamente. Quem já tem conta apenas ganha mais tempo —
          a renovação soma ao vencimento atual, não recomeça de hoje.
        </Typography>
      </Paper>
    </MainContainer>
  );
};

export default EiPaguei;
